import PropTypes from 'prop-types'
import { forwardRef, memo, useMemo } from 'react'
import classNames from 'lib/util/classNames'
import Icon from '../Icon/Icon'
import styles from './Button.module.scss'

/**
 * The `ButtonContextSymbol`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const ButtonContextSymbol = forwardRef((props, forwardedRef) => {
  const {
    ElementTag,
    children,
    className,
    icon,
    isBusy,
    isHover,
    side,
    useBreakpointCollapse,
    useHighlight,
    useHighlightHover,
    ...rest
  } = props
  const attributes = useMemo(
    () => ({
      className: classNames([
        styles.button,
        styles.buttonTypeContextSymbol,
        className,
        styles[side],
        useBreakpointCollapse && styles.useBreakpointCollapse
      ])
    }),
    [className]
  )

  return (
    <ElementTag ref={forwardedRef} {...attributes} {...rest}>
      <div className={styles.buttonBase}>
        <div className={styles.buttonBg} />
        <div className={styles.buttonSymbol}>
          <Icon icon={icon} />
        </div>
      </div>
      <div className={styles.buttonContent}>{children}</div>
    </ElementTag>
  )
})

// Display name
ButtonContextSymbol.displayName = 'ButtonContextSymbol'

/** @type {object} */
ButtonContextSymbol.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  ElementTag: PropTypes.elementType.isRequired,
  icon: PropTypes.string,
  side: PropTypes.string,
  useBreakpointCollapse: PropTypes.bool,
  isBusy: PropTypes.bool,
  isHover: PropTypes.bool,
  useHighlight: PropTypes.bool,
  useHighlightHover: PropTypes.bool
}

/** @type {object} */
ButtonContextSymbol.defaultProps = {
  children: [],
  className: '',
  side: 'left',
  icon: '',
  useBreakpointCollapse: false,
  isBusy: false,
  isHover: false,
  useHighlight: false,
  useHighlightHover: false
}

// Memoize
export default memo(ButtonContextSymbol)
